import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import "./PunchIn.scss";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";

// Constants
const HOURS_OPTIONS = Array.from({ length: 10 }, (_, i) => i);
const MINUTES_OPTIONS = [0, 15, 30, 45];

// Custom hook for location management
const useGeolocation = () => {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);

  const getCurrentLocation = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const error = new Error("Geolocation not supported");
        setLocationError(error.message);
        return reject(error);
      }

      setIsLoadingLocation(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude.toFixed(4),
            longitude: position.coords.longitude.toFixed(4),
          };
          setLocation(coords);
          setLocationError(null);
          setIsLoadingLocation(false);
          resolve(coords);
        },
        async (error) => {
          let message = "";
          switch (error.code) {
            case error.PERMISSION_DENIED:
              message = "Permission denied. Enable location access.";
              break;
            case error.POSITION_UNAVAILABLE:
              message = "Location unavailable. Using IP-based location.";
              try {
                const ipData = await fetch("https://ipapi.co/json/").then((r) =>
                  r.json()
                );
                const coords = {
                  latitude: ipData.latitude,
                  longitude: ipData.longitude,
                };
                setLocation(coords);
                setLocationError(null);
                resolve(coords);
                setIsLoadingLocation(false);
                return;
              } catch {
                message = "Failed to get location from IP.";
              }
              break;
            case error.TIMEOUT:
              message = "Location request timed out.";
              break;
            default:
              message = error.message || "Unknown location error.";
          }
          setLocationError(message);
          setIsLoadingLocation(false);
          reject(new Error(message));
        },
        {
          enableHighAccuracy: true,
          timeout: 20000,
          maximumAge: 0, // Force fresh location, don't use cached
        }
      );
    });
  }, []);

  return { location, locationError, isLoadingLocation, getCurrentLocation };
};

// Location Map Component
const LocationMap = ({ latitude, longitude, isLoading, error }) => {
  if (isLoading) {
    return (
      <div className="location-map-loading">
        <div className="loading-content">
          <div className="loading-icon">📍</div>
          <div className="loading-text">Getting your location...</div>
          <div className="loading-subtext">Please allow location access</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="location-map-error">
        <span className="error-icon">⚠️</span>
        <div className="error-content">
          <div className="error-title">Location Error</div>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  if (!latitude || !longitude) {
    return null;
  }

  const mapUrl = `https://www.openstreetmap.org/export/embed.html?bbox=${
    parseFloat(longitude) - 0.01
  },${parseFloat(latitude) - 0.01},${parseFloat(longitude) + 0.01},${
    parseFloat(latitude) + 0.01
  }&layer=mapnik&marker=${latitude},${longitude}`;

  return (
    <div className="location-map-container">
      <iframe
        width="100%"
        height="80"
        frameBorder="0"
        scrolling="no"
        marginHeight="0"
        marginWidth="0"
        src={mapUrl}
        title="Location Map"
        className="location-map-iframe"
      />

      <div className="location-map-footer">
        Location captured for attendance verification
      </div>
    </div>
  );
};

// Custom hook for attendance data management
const useAttendanceData = () => {
  const [attendanceTypes, setAttendanceTypes] = useState([]);
  const [officeEndTime, setOfficeEndTime] = useState(null);
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [isPunchIn, setIsPunchIn] = useState(false);
  const [punchInTime, setPunchInTime] = useState(null);
  const [attendanceId, setAttendanceId] = useState(null);
  const [error, setError] = useState("");
  const [earlyLogOut, setEarlyLogOut] = useState(false);
  const [lateLogOut, setLateLogOut] = useState(false);

  const fetchAttendanceTypes = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance-types`
      );
      const data = response.data?.data;
      setAttendanceTypes(Array.isArray(data) ? data : []);
    } catch (error) {
      setError("Failed to load attendance types.");
    }
  }, []);

  const fetchOfficeTimings = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/office-timings`
      );
      setOfficeEndTime(response.data.endTime);
    } catch (error) {
      setError("Failed to load office timings.");
    }
  }, []);

  const fetchTimesheetStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/company-settings/timesheet-Status`
      );
      const isRequired =
        response.data.data === true || response.data.data === "true";
      setShowTimesheet(isRequired);
    } catch (error) {
      setError("Failed to load timesheet data.");
    }
  }, []);

  const fetchPunchStatus = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/status`
      );

      const data = response.data?.data;
      if (!data) return;

      const attId = data.attendanceId;
      sessionStorage.setItem("attendanceId", attId);
      setAttendanceId(attId);

      setEarlyLogOut(data.earlyLogOut || false);
      setLateLogOut(data.lateLogOut || false);

      if (data.attendanceStatus) {
        setIsPunchIn(true);
        setPunchInTime(
          new Date(data.punchInTime.hour, data.punchInTime.minute)
        );
        return "attendanceActive";
      } else {
        setIsPunchIn(false);
        setPunchInTime(null);

        if (data.earlyLogOut) {
          return "Early Logout Detected";
        } else if (data.lateLogOut) {
          return "Late Logout Detected";
        }

        return "attendanceInactive";
      }
    } catch (error) {
      setError("Failed to fetch punch status.");
    }
  }, []);

  return {
    attendanceTypes,
    officeEndTime,
    showTimesheet,
    isPunchIn,
    punchInTime,
    attendanceId,
    error,
    earlyLogOut,
    lateLogOut,
    setError,
    setIsPunchIn,
    setPunchInTime,
    fetchAttendanceTypes,
    fetchOfficeTimings,
    fetchTimesheetStatus,
    fetchPunchStatus,
  };
};

// Custom hook for modal state management
const useModalState = () => {
  const [modals, setModals] = useState({
    punchIn: false,
    punchOut: false,
    earlyLogout: false,
    lateLogout: false,
  });

  const openModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: true }));
  }, []);

  const closeModal = useCallback((modalName) => {
    setModals((prev) => ({ ...prev, [modalName]: false }));
  }, []);

  return { modals, openModal, closeModal };
};

// Custom hook for form data management
const useFormData = () => {
  const [formData, setFormData] = useState({
    selectedAttendanceType: "",
    notes: "",
    punchOutMessage: "",
    earlyLogoutMessageToManager: "",
    earlyLogoutTime: "",
  });

  const updateFormData = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const resetFormData = useCallback(() => {
    setFormData({
      selectedAttendanceType: "",
      notes: "",
      punchOutMessage: "",
      earlyLogoutMessageToManager: "",
      earlyLogoutTime: "",
    });
  }, []);

  return { formData, updateFormData, resetFormData };
};

// Custom hook for loading states
const useLoadingStates = () => {
  const [loadingStates, setLoadingStates] = useState({
    punchIn: false,
    punchOut: false,
    earlyLogout: false,
    lateLogout: false,
  });

  const setLoading = useCallback((action, isLoading) => {
    setLoadingStates((prev) => ({ ...prev, [action]: isLoading }));
  }, []);

  const isAnyLoading = Object.values(loadingStates).some(Boolean);

  return { loadingStates, setLoading, isAnyLoading };
};

// Custom hook for project and timesheet management
const useProjectManagement = () => {
  const [projects, setProjects] = useState([{ id: 1, name: "Project 1" }]);
  const [timesheetEntries, setTimesheetEntries] = useState({});
  const [projectMessages, setProjectMessages] = useState({});
  const [projectNames, setProjectNames] = useState({});

  const handleTimesheetChange = useCallback((projectId, hours, minutes) => {
    setTimesheetEntries((prev) => ({
      ...prev,
      [projectId]: { hours, minutes },
    }));
  }, []);

  const handleProjectMessageChange = useCallback((projectId, message) => {
    setProjectMessages((prev) => ({
      ...prev,
      [projectId]: message,
    }));
  }, []);

  const handleProjectNameChange = useCallback((projectId, name) => {
    setProjectNames((prev) => ({
      ...prev,
      [projectId]: name,
    }));
  }, []);

  const handleAddProject = useCallback(() => {
    const newProjectId = projects.length + 1;
    setProjects((prev) => [
      ...prev,
      { id: newProjectId, name: `Project ${newProjectId}` },
    ]);
  }, [projects.length]);

  const handleRemoveProject = useCallback(
    (projectId) => {
      if (projects.length === 1) {
        toast.warning("At least one project is required.");
        return;
      }

      setProjects((prev) => prev.filter((project) => project.id !== projectId));

      setTimesheetEntries((prev) => {
        const updated = { ...prev };
        delete updated[projectId];
        return updated;
      });

      setProjectMessages((prev) => {
        const updated = { ...prev };
        delete updated[projectId];
        return updated;
      });

      setProjectNames((prev) => {
        const updated = { ...prev };
        delete updated[projectId];
        return updated;
      });
    },
    [projects.length]
  );

  return {
    projects,
    timesheetEntries,
    projectMessages,
    projectNames,
    handleTimesheetChange,
    handleProjectMessageChange,
    handleProjectNameChange,
    handleAddProject,
    handleRemoveProject,
  };
};

// Modal Components
const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  className = "",
  size = "medium",
}) => {
  if (!isOpen) return null;

  const sizeClasses = {
    small: "max-w-md",
    medium: "max-w-2xl",
    large: "max-w-4xl",
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={`modal-content ${sizeClasses[size]} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close-button" onClick={onClose}>
          &times;
        </button>
        <h3 className="modal-title">{title}</h3>
        {children}
      </div>
    </div>
  );
};

const AttendanceTypeSelector = ({
  attendanceTypes,
  selectedOption,
  onSelectionChange,
  disabled = false,
}) => (
  <div className="attendance-type-selector">
    <h4>Select Attendance Type</h4>
    <div className="attendance-options">
      {attendanceTypes.map((type) => (
        <button
          key={type.attendanceTypeId}
          className={`attendance-option ${
            selectedOption === type.attendanceTypeId ? "selected" : ""
          }`}
          onClick={() => onSelectionChange(type.attendanceTypeId)}
          disabled={disabled}
        >
          {type.attendanceType}
        </button>
      ))}
    </div>
  </div>
);

const TimesheetSection = ({
  projects,
  timesheetEntries,
  projectMessages,
  projectNames,
  onTimesheetChange,
  onProjectMessageChange,
  onProjectNameChange,
  onAddProject,
  onRemoveProject,
  isVisible = true,
  showProjectNameInput = false,
  disabled = false,
}) => {
  if (!isVisible) return null;

  return (
    <div className="timesheet-section">
      <h3>Timesheet</h3>
      {projects.map((project, index) => (
        <div key={project.id} className="project-entry relative">
          {index > 0 && (
            <button
              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-sm font-bold hover:bg-red-600 transition-colors"
              onClick={() => onRemoveProject(project.id)}
              title="Remove this project"
              disabled={disabled}
            >
              ✕
            </button>
          )}

          {showProjectNameInput ? (
            <input
              type="text"
              className="project-name-input"
              placeholder="Enter project name"
              value={projectNames[project.id] || ""}
              onChange={(e) => onProjectNameChange(project.id, e.target.value)}
              disabled={disabled}
            />
          ) : (
            <span className="project-name">{project.name}</span>
          )}

          <div className="time-message-container">
            <div className="time-inputs">
              <div className="time-section">
                <label>Hours</label>
                <div className="time-buttons">
                  {HOURS_OPTIONS.map((hour) => (
                    <button
                      key={hour}
                      type="button"
                      className={`time-btn ${
                        timesheetEntries[project.id]?.hours === hour
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        onTimesheetChange(
                          project.id,
                          hour,
                          timesheetEntries[project.id]?.minutes || 0
                        )
                      }
                      disabled={disabled}
                    >
                      {hour}h
                    </button>
                  ))}
                </div>
              </div>

              <div className="time-section">
                <label>Minutes</label>
                <div className="time-buttons">
                  {MINUTES_OPTIONS.map((minute) => (
                    <button
                      key={minute}
                      type="button"
                      className={`time-btn ${
                        timesheetEntries[project.id]?.minutes === minute
                          ? "active"
                          : ""
                      }`}
                      onClick={() =>
                        onTimesheetChange(
                          project.id,
                          timesheetEntries[project.id]?.hours || 0,
                          minute
                        )
                      }
                      disabled={disabled}
                    >
                      {minute}m
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <textarea
              className="task-description"
              placeholder="Task description"
              value={projectMessages[project.id] || ""}
              onChange={(e) =>
                onProjectMessageChange(project.id, e.target.value)
              }
              disabled={disabled}
            />
          </div>
        </div>
      ))}

      <button
        onClick={onAddProject}
        className="add-project-btn"
        disabled={disabled}
      >
        + Add Project
      </button>
    </div>
  );
};

// Utility functions
const validateTimesheetData = (
  projects,
  timesheetEntries,
  projectNames,
  isTimesheetRequired
) => {
  if (!isTimesheetRequired) return { isValid: true, errors: [] };

  const errors = [];
  for (const project of projects) {
    const projectTimesheet = timesheetEntries[project.id];
    const projectName = projectNames[project.id] || "";

    if (
      !projectTimesheet ||
      projectTimesheet.hours === "" ||
      projectTimesheet.minutes === "" ||
      !projectName.trim()
    ) {
      errors.push(
        `Please complete timesheet for: ${
          projectName || "Project " + project.id
        }`
      );
    }
  }

  return { isValid: errors.length === 0, errors };
};

const createWorkTasksPayload = (
  projects,
  timesheetEntries,
  projectMessages,
  projectNames
) => {
  return projects.map((project) => ({
    workTaskId: 0,
    projectName: projectNames[project.id] || project.name,
    task: projectMessages[project.id] || "",
    workedHours: timesheetEntries[project.id]?.hours || 0,
    workedMinutes: timesheetEntries[project.id]?.minutes || 0,
  }));
};

const handleApiError = (error, operation, setError, toast) => {
  const message =
    error.response?.data?.message || `An error occurred during ${operation}`;

  if (error.response?.data?.status === 409) {
    toast.error(error.response?.data?.message);
  } else {
    setError(message);
  }
};

const formatTo12Hour = (time) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "pm" : "am";
  const formattedHour = (hour % 12 || 12).toString().padStart(2, "0");
  return `${formattedHour}:${minutes} ${ampm}`;
};

// Main Component
const PunchIn = () => {
  const {
    attendanceTypes,
    officeEndTime,
    showTimesheet,
    isPunchIn,
    punchInTime,
    attendanceId,
    error,
    earlyLogOut,
    lateLogOut,
    setError,
    setIsPunchIn,
    setPunchInTime,
    fetchAttendanceTypes,
    fetchOfficeTimings,
    fetchTimesheetStatus,
    fetchPunchStatus,
  } = useAttendanceData();

  const { location, locationError, isLoadingLocation, getCurrentLocation } =
    useGeolocation();
  const { modals, openModal, closeModal } = useModalState();
  const { formData, updateFormData, resetFormData } = useFormData();
  const { loadingStates, setLoading, isAnyLoading } = useLoadingStates();
  const {
    projects,
    timesheetEntries,
    projectMessages,
    projectNames,
    handleTimesheetChange,
    handleProjectMessageChange,
    handleProjectNameChange,
    handleAddProject,
    handleRemoveProject,
  } = useProjectManagement();

  useEffect(() => {
    const initializeData = async () => {
      await Promise.all([
        fetchAttendanceTypes(),
        fetchOfficeTimings(),
        fetchTimesheetStatus(),
      ]);

      const message = await fetchPunchStatus();
      handlePunchStatusMessage(message);
    };

    initializeData();
  }, [
    fetchAttendanceTypes,
    fetchOfficeTimings,
    fetchTimesheetStatus,
    fetchPunchStatus,
  ]);

  const handlePunchStatusMessage = async (message) => {
    switch (message) {
      case "Early Logout Detected":
        openModal("earlyLogout");
        // Capture location when early logout modal opens
        try {
          await getCurrentLocation();
        } catch (error) {
          console.error("Location error:", error.message);
        }
        break;
      case "Late Logout Detected":
        openModal("lateLogout");
        // Capture location when late logout modal opens
        try {
          await getCurrentLocation();
        } catch (error) {
          console.error("Location error:", error.message);
        }
        break;
      default:
        break;
    }
  };

  const handleOpenPunchInModal = async () => {
    openModal("punchIn");
    try {
      await getCurrentLocation();
    } catch (error) {
      console.error("Location error:", error.message);
      // Modal will show the error via LocationMap component
    }
  };

  const handleOpenPunchOutModal = async () => {
    openModal("punchOut");
    try {
      await getCurrentLocation();
    } catch (error) {
      console.error("Location error:", error.message);
      // Modal will show the error via LocationMap component
    }
  };

  const handlePunchIn = async () => {
    if (loadingStates.punchIn) return;

    if (!formData.selectedAttendanceType) {
      setError("Please select an attendance type.");
      return;
    }

    if (!location) {
      toast.error("Location is required for punch-in");
      return;
    }

    setLoading("punchIn", true);

    try {
      const workTasks = createWorkTasksPayload(
        projects,
        timesheetEntries,
        projectMessages,
        projectNames
      );

      const currentTime = new Date();
      const punchInData = {
        attendanceId: 0,
        attendanceTypeId: formData.selectedAttendanceType,
        punchInTime: currentTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        punchInMessage: formData.notes,
        latitude: location.latitude,
        longitude: location.longitude,
        workTasks,
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/punch-in`,
        punchInData
      );

      if (response.data.status === 200) {
        // Store the attendanceId from the response
        const responseAttendanceId = response.data.data?.attendanceId;
        if (responseAttendanceId) {
          sessionStorage.setItem("attendanceId", responseAttendanceId);
          // Also update the state in useAttendanceData hook if needed
        }

        toast.success("Punch In successful");
        setPunchInTime(currentTime);
        setIsPunchIn(true);
        closeModal("punchIn");
        resetFormData();
      }
    } catch (error) {
      handleApiError(error, "punch-in", setError, toast);
    } finally {
      setLoading("punchIn", false);
    }
  };

  const handlePunchOut = async () => {
    if (loadingStates.punchOut) return;

    const validation = validateTimesheetData(
      projects,
      timesheetEntries,
      projectNames,
      showTimesheet
    );

    if (!validation.isValid) {
      validation.errors.forEach((error) => toast.error(error));
      return;
    }

    if (!location) {
      toast.error("Location is required for punch-out");
      return;
    }

    setLoading("punchOut", true);

    try {
      const currentTime = new Date();
      // Format date in local timezone (YYYY-MM-DD)
      const year = currentTime.getFullYear();
      const month = String(currentTime.getMonth() + 1).padStart(2, "0");
      const day = String(currentTime.getDate()).padStart(2, "0");
      const localDate = `${year}-${month}-${day}`;

      const punchOutData = {
        attendanceId:
          Number(sessionStorage.getItem("attendanceId")) || attendanceId,
        punchOutTime: currentTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        punchOutDate: localDate,
        punchOutMessage: formData.punchOutMessage || "Punch out completed",
        latitude: location.latitude,
        longitude: location.longitude,
        workTasks: createWorkTasksPayload(
          projects,
          timesheetEntries,
          projectMessages,
          projectNames
        ),
      };

      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/punch-out`,
        punchOutData
      );

      if (response.status === 200) {
        const responseData = response.data.data;

        if (responseData.attendanceStatus === false) {
          if (responseData.earlyLogOut === true) {
            closeModal("punchOut");
            openModal("earlyLogout");
          } else if (responseData.lateLogOut === true) {
            closeModal("punchOut");
            openModal("lateLogout");
          }
        } else if (responseData.attendanceStatus === true) {
          toast.success("Punch Out successful");
          sessionStorage.removeItem("punchInTime");
          sessionStorage.setItem("attendanceStatus", "false");
          setIsPunchIn(false);
          closeModal("punchOut");
          setPunchInTime(null);
          updateFormData("punchOutMessage", "");
        }
      }
    } catch (error) {
      handleApiError(error, "punch-out", setError, toast);
    } finally {
      setLoading("punchOut", false);
    }
  };

  const handleEarlyLogoutSubmit = async () => {
    if (loadingStates.earlyLogout) return;

    // Validate location before proceeding
    if (!location) {
      toast.error("Location is required for early logout");
      return;
    }

    setLoading("earlyLogout", true);

    const workTasks = createWorkTasksPayload(
      projects,
      timesheetEntries,
      projectMessages,
      projectNames
    );
    const formattedPunchOutTime = formatTo12Hour(formData.earlyLogoutTime);

    // Get current date in local timezone
    const currentTime = new Date();
    const year = currentTime.getFullYear();
    const month = String(currentTime.getMonth() + 1).padStart(2, "0");
    const day = String(currentTime.getDate()).padStart(2, "0");
    const localDate = `${year}-${month}-${day}`;

    const earlyLogoutData = {
      attendanceId: Number(sessionStorage.getItem("attendanceId")),
      punchOutTime: formattedPunchOutTime,
      punchOutDate: localDate,
      latitude: location.latitude,
      longitude: location.longitude,
      managerRequestMessage: formData.earlyLogoutMessageToManager,
      punchOutMessage: formData.punchOutMessage,
      workTasks,
    };

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/request-early-punchout`,
        earlyLogoutData
      );

      if (response.status === 200) {
        sessionStorage.removeItem("punchInTime");
        sessionStorage.setItem("attendanceStatus", "false");
        toast.success("Early Punch-Out request sent to manager");
        closeModal("earlyLogout");
        setIsPunchIn(false);
        setPunchInTime(null);
      }
    } catch (error) {
      handleApiError(error, "early logout", setError, toast);
    } finally {
      setLoading("earlyLogout", false);
    }
  };

  const handleLateLogoutSubmit = async () => {
    if (loadingStates.lateLogout) return;

    // Validate location before proceeding
    if (!location) {
      toast.error("Location is required for late logout");
      return;
    }

    setLoading("lateLogout", true);

    const workTasks = createWorkTasksPayload(
      projects,
      timesheetEntries,
      projectMessages,
      projectNames
    );
    const formattedPunchOutTime = formatTo12Hour(formData.earlyLogoutTime);

    // Get current date in local timezone
    const currentTime = new Date();
    const year = currentTime.getFullYear();
    const month = String(currentTime.getMonth() + 1).padStart(2, "0");
    const day = String(currentTime.getDate()).padStart(2, "0");
    const localDate = `${year}-${month}-${day}`;

    const lateLogoutData = {
      attendanceId: Number(sessionStorage.getItem("attendanceId")),
      punchOutTime: formattedPunchOutTime,
      punchOutDate: localDate,
      latitude: location.latitude,
      longitude: location.longitude,
      managerRequestMessage: formData.earlyLogoutMessageToManager,
      punchOutMessage: formData.punchOutMessage,
      workTasks,
    };

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/attendance/request-late-punchout`,
        lateLogoutData
      );

      if (response.status === 200) {
        sessionStorage.removeItem("punchInTime");
        sessionStorage.setItem("attendanceStatus", "false");
        toast.success("Late Punch-Out request sent to manager");
        closeModal("lateLogout");
        setIsPunchIn(false);
        setPunchInTime(null);
      }
    } catch (error) {
      handleApiError(error, "late logout", setError, toast);
    } finally {
      setLoading("lateLogout", false);
    }
  };

  return (
    <div className="punch-in-container">
      <ButtonWithIcon
        onClick={() =>
          isPunchIn ? handleOpenPunchOutModal() : handleOpenPunchInModal()
        }
        text=""
        ariaLabel={isPunchIn ? "Punch out" : "Punch in"}
        icon="material-symbols:fingerprint-sharp"
        className={`punch-button ${isPunchIn ? "punch-out" : "punch-in"}`}
        disabled={isAnyLoading}
      />

      {/* Punch In Modal */}
      <Modal
        isOpen={modals.punchIn}
        onClose={() => !loadingStates.punchIn && closeModal("punchIn")}
        title="Punch In"
        size="large"
      >
        <LocationMap
          latitude={location?.latitude}
          longitude={location?.longitude}
          isLoading={isLoadingLocation}
          error={locationError}
        />

        <AttendanceTypeSelector
          attendanceTypes={attendanceTypes}
          selectedOption={formData.selectedAttendanceType}
          onSelectionChange={(typeId) =>
            updateFormData("selectedAttendanceType", typeId)
          }
          disabled={loadingStates.punchIn}
        />

        {error && <div className="error-message">{error}</div>}

        <textarea
          className="notes-input"
          placeholder="Add personal notes here, e.g., 'Worked on project tasks, next steps pending.'"
          value={formData.notes}
          onChange={(e) => updateFormData("notes", e.target.value)}
          disabled={loadingStates.punchIn}
        />

        <TimesheetSection
          projects={projects}
          timesheetEntries={timesheetEntries}
          projectMessages={projectMessages}
          projectNames={projectNames}
          onTimesheetChange={handleTimesheetChange}
          onProjectMessageChange={handleProjectMessageChange}
          onProjectNameChange={handleProjectNameChange}
          onAddProject={handleAddProject}
          onRemoveProject={handleRemoveProject}
          isVisible={showTimesheet}
          showProjectNameInput={true}
          disabled={loadingStates.punchIn}
        />

        <div className="modal-actions">
          <ButtonWithIcon
            onClick={handlePunchIn}
            text={loadingStates.punchIn ? "Processing..." : "Punch In"}
            icon="material-symbols:fingerprint-sharp"
            className="primary"
            disabled={loadingStates.punchIn || !location}
          />
        </div>
      </Modal>

      {/* Punch Out Modal */}
      <Modal
        isOpen={modals.punchOut}
        onClose={() => !loadingStates.punchOut && closeModal("punchOut")}
        title="Punch Out"
        size="large"
      >
        <LocationMap
          latitude={location?.latitude}
          longitude={location?.longitude}
          isLoading={isLoadingLocation}
          error={locationError}
        />

        <textarea
          className="notes-textarea"
          placeholder="Punch Out Message"
          value={formData.punchOutMessage}
          onChange={(e) => updateFormData("punchOutMessage", e.target.value)}
          disabled={loadingStates.punchOut}
        />

        <TimesheetSection
          projects={projects}
          timesheetEntries={timesheetEntries}
          projectMessages={projectMessages}
          projectNames={projectNames}
          onTimesheetChange={handleTimesheetChange}
          onProjectMessageChange={handleProjectMessageChange}
          onProjectNameChange={handleProjectNameChange}
          onAddProject={handleAddProject}
          onRemoveProject={handleRemoveProject}
          isVisible={showTimesheet}
          showProjectNameInput={true}
          disabled={loadingStates.punchOut}
        />

        <div className="modal-actions">
          <ButtonWithIcon
            onClick={handlePunchOut}
            text={loadingStates.punchOut ? "Processing..." : "Punch Out"}
            className="cancel-button"
            icon="material-symbols:fingerprint-sharp"
            disabled={loadingStates.punchOut || !location}
          />
        </div>
      </Modal>

      {/* Early Logout Modal */}
      {modals.earlyLogout && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="timesheet-close-button"
              onClick={() =>
                !loadingStates.earlyLogout && closeModal("earlyLogout")
              }
              disabled={loadingStates.earlyLogout}
            >
              &times;
            </button>
            <h3>Early Logout Request to Manager</h3>

            {/* Add LocationMap component */}
            <LocationMap
              latitude={location?.latitude}
              longitude={location?.longitude}
              isLoading={isLoadingLocation}
              error={locationError}
            />

            <label>Punch out time</label>
            <input
              type="time"
              placeholder="Punch Out Time"
              value={formData.earlyLogoutTime}
              onChange={(e) =>
                updateFormData("earlyLogoutTime", e.target.value)
              }
              disabled={loadingStates.earlyLogout}
            />
            <textarea
              placeholder="Message to Manager"
              value={formData.earlyLogoutMessageToManager}
              onChange={(e) =>
                updateFormData("earlyLogoutMessageToManager", e.target.value)
              }
              disabled={loadingStates.earlyLogout}
            />
            <div className="button-group">
              <button
                onClick={handleEarlyLogoutSubmit}
                disabled={loadingStates.earlyLogout || !location}
              >
                {loadingStates.earlyLogout
                  ? "Processing..."
                  : "Submit Early Logout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Late Logout Modal */}
      {modals.lateLogout && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button
              className="timesheet-close-button"
              onClick={() =>
                !loadingStates.lateLogout && closeModal("lateLogout")
              }
              disabled={loadingStates.lateLogout}
            >
              &times;
            </button>
            <h3>Late Logout Request to Manager</h3>

            {/* Add LocationMap component */}
            <LocationMap
              latitude={location?.latitude}
              longitude={location?.longitude}
              isLoading={isLoadingLocation}
              error={locationError}
            />

            <label>Punch out time</label>
            <input
              type="time"
              placeholder="Punch Out Time"
              value={formData.earlyLogoutTime}
              onChange={(e) =>
                updateFormData("earlyLogoutTime", e.target.value)
              }
              disabled={loadingStates.lateLogout}
            />
            <textarea
              placeholder="Message to Manager"
              value={formData.earlyLogoutMessageToManager}
              onChange={(e) =>
                updateFormData("earlyLogoutMessageToManager", e.target.value)
              }
              disabled={loadingStates.lateLogout}
            />
            <div className="button-group">
              <button
                onClick={handleLateLogoutSubmit}
                disabled={loadingStates.lateLogout || !location}
              >
                {loadingStates.lateLogout
                  ? "Processing..."
                  : "Submit Late Logout"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PunchIn;
